import { useEffect, useRef } from "react"
import { Link } from "@inertiajs/react"
import type { SearchResult } from "@/types/v2/search"
import { ChevronRightIcon } from "@/lib/inbox-icons"
import { ResultLeadingIcon } from "./result-leading-icon"

// One result row in the command palette (CON-183).
//
// Renders a real `<Link>` so a plain click / tap navigates
// the Inertia way (no `<a>`, no manual `router.visit` for the
// pointer path). The palette's keyboard handler drives the
// `active` highlight + Enter navigation on top of this.
//
// Overflow discipline (CLAUDE.md "Lists must … clip
// overflow"): every intermediate flex column carries
// `min-w-0`, and the text nodes `truncate`, so a single long
// title / subtitle / snippet can't push the row wider than
// the pane.

interface SearchResultRowProps {
  result: SearchResult
  // Highlighted by keyboard nav (desktop) — drives the cream
  // background + scroll-into-view + `aria-selected`.
  active: boolean
  // Pointer hover sets this row active (so the preview pane
  // follows the mouse, matching keyboard nav).
  onHover: () => void
  // Click handler — records the open into recents. Navigation
  // itself is the `<Link>`'s job; this fires alongside it.
  onActivate: () => void
  // Mobile rows show a trailing chevron and tap-to-navigate;
  // desktop rows omit the chevron (the preview pane is the
  // affordance).
  showChevron?: boolean
}

export function SearchResultRow({
  result,
  active,
  onHover,
  onActivate,
  showChevron = false,
}: SearchResultRowProps) {
  const ref = useRef<HTMLAnchorElement | null>(null)

  // Keep the active row visible as ↑/↓ walks past the
  // viewport edge. `scrollIntoView` with `block: "nearest"`
  // doesn't yank the list when the row is already visible.
  // jsdom doesn't implement `scrollIntoView`, so guard on the
  // method's presence.
  useEffect(() => {
    const el = ref.current
    if (active && el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "nearest" })
    }
  }, [active])

  return (
    <Link
      ref={ref}
      href={result.href}
      onClick={onActivate}
      onMouseEnter={onHover}
      aria-selected={active}
      data-testid={`search-result-${result.key}`}
      data-active={active || undefined}
      className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-left ${
        active ? "bg-[#FBF3EA]" : "hover:bg-[#FBF8F1]"
      }`}
    >
      <ResultLeadingIcon type={result.type} title={result.title} />
      <div className="flex min-w-0 grow flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <span className="min-w-0 grow truncate text-[13.5px] leading-[18px] font-medium text-[#1E1B17]">
            {result.title}
          </span>
          {result.timestampLabel ? (
            <span className="shrink-0 text-[11px] leading-3.5 text-[#9A938A]">
              {result.timestampLabel}
            </span>
          ) : null}
        </div>
        <span className="min-w-0 truncate text-[12px] leading-4 text-[#7A746B]">
          {result.subtitle}
        </span>
        {result.snippet ? (
          <span className="min-w-0 truncate text-[12px] leading-4 italic text-[#9A938A]">
            &ldquo;{result.snippet}&rdquo;
          </span>
        ) : null}
      </div>
      {showChevron ? (
        <span className="shrink-0">
          <ChevronRightIcon size={14} stroke="#C9C2B6" />
        </span>
      ) : null}
    </Link>
  )
}
